import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Alert,
    Snackbar,
    Typography,
    Box,
    Chip,
    IconButton,
    TablePagination,
    InputAdornment
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Visibility as ViewIcon, Delete as DeleteIcon, GetApp as ExportIcon, Business as EnterpriseIcon, Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import api from '../../services/api';
import { usePermission } from '../../hooks/usePermission';
import './AdminPackageManagement.css';

const AdminPackageManagement = () => {
    const { checkPermission } = usePermission();
    const [packages, setPackages] = useState([]);
    const [dashboardData, setDashboardData] = useState({});
    const [loading, setLoading] = useState(true);
    const [showPackageModal, setShowPackageModal] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const [packageTypes, setPackageTypes] = useState([]);
    
    // Pagination state - using same pattern as FeedbackManagement.js
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    
    // Search and filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [searchLoading, setSearchLoading] = useState(false); // Loading state for search button
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportData, setExportData] = useState(null);
    const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
    const [enterpriseData, setEnterpriseData] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    // Form state — property names mirror the API (credits_*, min_credits, …); UI copy uses “tokens”.
    const [packageForm, setPackageForm] = useState({
        name: '',
        credits: '',
        price: '',
        validity_days: '',
        package_type: 'PAID',
        description: '',
        is_active: true,
        sort_order: 0,
        is_enterprise: false,
        min_credits: null,
        max_credits: null,
        custom_terms: '',
        credit_validity_days: '',
        credit_expiry_extension_days: 0,
        bulk_discount_threshold: null,
        bulk_discount_percentage: null
    });

    useEffect(() => {
        loadData();
        loadPackageTypes();
    }, [page, rowsPerPage]);

    const loadData = async () => {
        try {
            setLoading(true);
            
            // Load packages - using same pagination pattern as FeedbackManagement.js
            const packagesResponse = await api.get('/subscription/packages', {
                params: {
                    page: page + 1, // Backend expects 1-based page numbers
                    per_page: rowsPerPage,
                    search: searchTerm,
                    package_type: filterType,
                    is_active: filterStatus
                }
            });
            
            setPackages(packagesResponse.data.packages);
            setTotalCount(packagesResponse.data.pagination.total || 0);
            
        } catch (error) {
            console.error('Error loading packages:', error);
            showAlert('Error loading packages', 'error');
        } finally {
            setLoading(false);
        }
    };

    const loadPackageTypes = async () => {
        try {
            const response = await api.get('/subscription/packages/types');
            setPackageTypes(response.data.package_types);
        } catch (error) {
            console.error('Error loading package types:', error);
        }
    };

    // Search handler function - only searches when button is clicked
    const handleSearchPackages = async () => {
        setSearchLoading(true);
        // Reset to first page when searching
        setPage(0);
        try {
            // Load packages with current search term and filters
            await loadData();
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle page change - same pattern as FeedbackManagement.js
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        // Load specific page when pagination changes
        loadData();
    };

    // Handle rows per page change - same pattern as FeedbackManagement.js
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        // Load first page when rows per page changes
        loadData();
    };

    // Client-side validation functions
    const validateField = (fieldName, value) => {
        switch (fieldName) {
            case 'name':
                if (!value || !value.trim()) return 'Package name is required';
                if (value.trim().length < 2) return 'Package name must be at least 2 characters';
                if (value.trim().length > 100) return 'Package name must be less than 100 characters';
                return '';
            case 'credits':
                if (!value || value === '') return 'Tokens are required';
                const credits = parseInt(value);
                if (isNaN(credits)) return 'Tokens must be a valid number';
                if (credits < 1) return 'Tokens must be at least 1';
                if (credits > 1000000) return 'Tokens cannot exceed 1,000,000';
                return '';
            case 'price':
                // Skip price validation for FREE packages
                if (packageForm.package_type === 'FREE') return '';
                if (!value || value === '') return 'Price is required for paid packages';
                const price = parseFloat(value);
                if (isNaN(price)) return 'Price must be a valid number';
                if (price < 0) return 'Price cannot be negative';
                if (price > 100000) return 'Price cannot exceed 100,000';
                return '';
            case 'validity_days':
                if (!value || value === '') return 'Validity days is required';
                const validity = parseInt(value);
                if (isNaN(validity)) return 'Validity must be a valid number';
                if (validity < 1) return 'Validity must be at least 1 day';
                if (validity > 3650) return 'Validity cannot exceed 10 years';
                return '';
            case 'credit_validity_days':
                if (!value || value === '') return 'Token validity (days) is required';
                const creditValidity = parseInt(value);
                if (isNaN(creditValidity)) return 'Token validity must be a valid number';
                if (creditValidity < 1) return 'Token validity must be at least 1 day';
                if (creditValidity > 3650) return 'Token validity cannot exceed 10 years';
                return '';
            case 'credit_expiry_extension_days':
                if (value === '' || value === null || value === undefined) return '';
                const extension = parseInt(value);
                if (isNaN(extension)) return 'Extension days must be a valid number';
                if (extension < 0) return 'Extension days cannot be negative';
                if (extension > 365) return 'Extension days cannot exceed 365';
                return '';
            case 'sort_order':
                if (value === '' || value === null || value === undefined) return '';
                const sortOrder = parseInt(value);
                if (isNaN(sortOrder)) return 'Sort order must be a valid number';
                if (sortOrder < 0) return 'Sort order cannot be negative';
                if (sortOrder > 999) return 'Sort order cannot exceed 999';
                return '';
            case 'min_credits':
                if (value === '' || value === null || value === undefined) return '';
                const minCredits = parseInt(value);
                if (isNaN(minCredits)) return 'Minimum tokens must be a valid number';
                if (minCredits < 1) return 'Minimum tokens must be at least 1';
                if (minCredits > 1000000) return 'Minimum tokens cannot exceed 1,000,000';
                return '';
            case 'max_credits':
                if (value === '' || value === null || value === undefined) return '';
                const maxCredits = parseInt(value);
                if (isNaN(maxCredits)) return 'Maximum tokens must be a valid number';
                if (maxCredits < 1) return 'Maximum tokens must be at least 1';
                if (maxCredits > 1000000) return 'Maximum tokens cannot exceed 1,000,000';
                return '';
            default:
                return '';
        }
    };

    const handleFieldChange = (fieldName, value) => {
        setPackageForm(prev => ({ ...prev, [fieldName]: value }));
        
        // Clear field errors when user types
        setFieldErrors(prev => ({
            ...prev,
            [fieldName]: ''
        }));
    };

    const handlePackageSubmit = async (e) => {
        e.preventDefault();
        
        // Client-side validation
        const nameError = validateField('name', packageForm.name);
        const creditsError = validateField('credits', packageForm.credits);
        const priceError = validateField('price', packageForm.price);
        const validityError = validateField('validity_days', packageForm.validity_days);
        const creditValidityError = validateField('credit_validity_days', packageForm.credit_validity_days);
        const extensionError = validateField('credit_expiry_extension_days', packageForm.credit_expiry_extension_days);
        const sortOrderError = validateField('sort_order', packageForm.sort_order);
        const minCreditsError = validateField('min_credits', packageForm.min_credits);
        const maxCreditsError = validateField('max_credits', packageForm.max_credits);
        
        // Additional validation for enterprise packages
        let enterpriseError = '';
        if (packageForm.is_enterprise && packageForm.min_credits && packageForm.max_credits) {
            const minCredits = parseInt(packageForm.min_credits);
            const maxCredits = parseInt(packageForm.max_credits);
            if (!isNaN(minCredits) && !isNaN(maxCredits) && minCredits > maxCredits) {
                enterpriseError = 'Minimum tokens cannot be greater than maximum tokens';
            }
        }
        
        if (nameError || creditsError || priceError || validityError || creditValidityError || 
            extensionError || sortOrderError || minCreditsError || maxCreditsError || enterpriseError) {
            setFieldErrors({
                name: nameError,
                credits: creditsError,
                price: priceError,
                validity_days: validityError,
                credit_validity_days: creditValidityError,
                credit_expiry_extension_days: extensionError,
                sort_order: sortOrderError,
                min_credits: minCreditsError,
                max_credits: maxCreditsError,
                enterprise: enterpriseError,
            });
            return;
        }
        
        try {
            
            // Ensure FREE packages have price 0
            if (packageForm.package_type === 'FREE' && packageForm.price !== '0' && packageForm.price !== 0) {
                packageForm.price = '0';
            }
            
            // Set default token validity (API: credit_validity_days) if not specified
            if (!packageForm.credit_validity_days && packageForm.validity_days) {
                packageForm.credit_validity_days = packageForm.validity_days;
            }
            
            // Clean up form data - convert empty strings to null for numeric fields
            const cleanFormData = {
                ...packageForm,
                credits: packageForm.credits || null,
                // Handle price field properly - FREE packages should have price 0, not null
                price: packageForm.package_type === 'FREE' ? 0 : (packageForm.price || null),
                validity_days: packageForm.validity_days || null,
                sort_order: packageForm.sort_order || 0,
                min_credits: packageForm.min_credits || null,
                max_credits: packageForm.max_credits || null,
                credit_validity_days: packageForm.credit_validity_days || null,
                credit_expiry_extension_days: packageForm.credit_expiry_extension_days || 0,
                bulk_discount_threshold: packageForm.bulk_discount_threshold || null,
                bulk_discount_percentage: packageForm.bulk_discount_percentage || null
            };
            
            if (editingPackage) {
                await api.put(`/subscription/packages/${editingPackage.id}`, cleanFormData);
                showAlert('Package updated successfully');
            } else {
                await api.post('/subscription/packages', cleanFormData);
                showAlert('Package created successfully');
            }
            
            setShowPackageModal(false);
            resetPackageForm();
            loadData();
            
        } catch (error) {
            console.error('Error saving package:', error);
            showAlert(error.response?.data?.error || 'Error saving package', 'error');
        }
    };

    const handlePackageEdit = (pkg) => {
        setEditingPackage(pkg);
        setPackageForm({
            name: pkg.name,
            credits: pkg.credits,
            price: pkg.price,
            validity_days: pkg.validity_days,
            package_type: pkg.package_type,
            description: pkg.description || '',
            is_active: pkg.is_active,
            sort_order: pkg.sort_order || 0,
            is_enterprise: pkg.is_enterprise || false,
            min_credits: pkg.min_credits || null,
            max_credits: pkg.max_credits || null,
            custom_terms: pkg.custom_terms || '',
            credit_validity_days: pkg.credit_validity_days || pkg.validity_days || '',
            credit_expiry_extension_days: pkg.credit_expiry_extension_days || 0,
            bulk_discount_threshold: pkg.bulk_discount_threshold || null,
            bulk_discount_percentage: pkg.bulk_discount_percentage || null
        });
        setFieldErrors({});
        setShowPackageModal(true);
    };

    const handlePackageToggle = async (packageId, currentStatus) => {
        try {
            await api.post(`/subscription/packages/${packageId}/toggle`);
            showAlert(`Package ${currentStatus ? 'deactivated' : 'activated'} successfully`);
            loadData();
        } catch (error) {
            console.error('Error toggling package:', error);
            showAlert('Error toggling package status', 'error');
        }
    };

    const handlePackageDelete = async (packageId) => {
        if (window.confirm('Are you sure you want to delete this package? This action cannot be undone.')) {
            try {
                const response = await api.delete(`/subscription/packages/${packageId}`);
                if (response.data.success) {
                    showAlert('Package deleted successfully', 'success');
                    loadData();
                } else {
                    showAlert('Failed to delete package', 'error');
                }
            } catch (error) {
                console.error('Error deleting package:', error);
                showAlert('Error deleting package', 'error');
                loadData();
            }
        }
    };

    const handleExportPackages = async () => {
        try {
            const response = await api.get('/subscription/packages/export');
            if (response.data && response.data.success) {
                setExportData(response.data.data);
                setShowExportModal(true);
            } else {
                showAlert('Export failed', 'error');
            }
        } catch (error) {
            console.error('Error exporting packages:', error);
            showAlert('Error exporting packages', 'error');
        }
    };

    const handleEnterpriseManagement = async () => {
        try {
            const response = await api.get('/subscription/packages/enterprise');
            if (response.data && response.data.success) {
                setEnterpriseData(response.data.enterprise_packages);
                setShowEnterpriseModal(true);
            } else {
                showAlert('Failed to load enterprise packages', 'error');
            }
        } catch (error) {
            console.error('Error loading enterprise packages:', error);
            showAlert('Error loading enterprise packages', 'error');
        }
    };

    const resetPackageForm = () => {
        setPackageForm({
            name: '',
            credits: '',
            price: '',
            validity_days: '',
            package_type: 'PAID',
            description: '',
            is_active: true,
            sort_order: 0,
            is_enterprise: false,
            min_credits: null,
            max_credits: null,
            custom_terms: '',
            credit_validity_days: '',
            credit_expiry_extension_days: 0,
            bulk_discount_threshold: null,
            bulk_discount_percentage: null
        });
        setEditingPackage(null);
        setFieldErrors({});
    };

    const showAlert = (message, type = 'success') => {
        setAlert({ show: true, message, type });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    // en-IN grouping only; displayed in “tokens” columns (values still from pkg.credits).
    const formatCredits = (credits) => {
        return new Intl.NumberFormat('en-IN').format(credits);
    };

    const getPackageTypeBadge = (type) => {
        const typeMap = {
            'FREE': 'success',
            'PAID': 'primary',
            'CUSTOM': 'warning',
            'TOPUP': 'info'
        };
        return <Chip label={type} color={typeMap[type] || 'default'} />;
    };

    return (
        <div className="admin-package-container">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" component="h2">
                    Package Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {checkPermission('Package Management', 'export') && (
                        <Button 
                            variant="outlined" 
                            onClick={() => handleExportPackages()}
                            startIcon={<ExportIcon />}
                        >
                            Export
                        </Button>
                    )}
                    {checkPermission('Package Management', 'manage_enterprise') && (
                        <Button 
                            variant="outlined" 
                            onClick={() => handleEnterpriseManagement()}
                            startIcon={<EnterpriseIcon />}
                        >
                            Enterprise
                        </Button>
                    )}
                    {checkPermission('Package Management', 'create') && (
                        <Button 
                            variant="contained" 
                            onClick={() => setShowPackageModal(true)}
                            startIcon={<AddIcon />}
                        >
                            Add New Package
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Search packages..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by name, description..."
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Package Type</InputLabel>
                                <Select
                                    value={filterType}
                                    onChange={(e) => setFilterType(e.target.value)}
                                    label="Package Type"
                                >
                                    <MenuItem value="">All Types</MenuItem>
                                    {packageTypes.map(type => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="true">Active</MenuItem>
                                    <MenuItem value="false">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                startIcon={<SearchIcon />}
                                onClick={handleSearchPackages}
                                disabled={searchLoading}
                                sx={{ 
                                    height: '56px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    fontSize: '0.95rem'
                                }}
                            >
                                {searchLoading ? 'Searching...' : 'Search'}
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Packages Table - using same pattern as FeedbackManagement.js */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 600 }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Package Name</TableCell>
                                <TableCell align="right">Tokens</TableCell>
                                <TableCell align="right">Price</TableCell>
                                <TableCell align="center">Validity</TableCell>
                                <TableCell align="center">Token validity</TableCell>
                                <TableCell align="center">Type</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography>Loading packages...</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : packages.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No packages found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                packages.map((pkg) => (
                                    <TableRow key={pkg.id} hover>
                                        <TableCell>
                                            <Typography variant="subtitle2">
                                                {pkg.name}
                                            </Typography>
                                            {pkg.description && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {pkg.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="h6" color="primary">
                                                {formatCredits(pkg.credits)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="h6" color="success.main">
                                                {pkg.price === 0 ? 'FREE' : formatCurrency(pkg.price)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {pkg.validity_days} days
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {pkg.credit_validity_days} days
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {getPackageTypeBadge(pkg.package_type)}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip 
                                                label={pkg.is_active ? 'Active' : 'Inactive'}
                                                color={pkg.is_active ? 'success' : 'default'}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                justifyContent: 'center', 
                                                gap: 0.5,
                                                flexDirection: 'row',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {checkPermission('Package Management', 'update') && (
                                                    <IconButton 
                                                        color="primary"
                                                        onClick={() => handlePackageEdit(pkg)}
                                                        size="small"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                )}
                                                {checkPermission('Package Management', 'toggle_status') && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color={pkg.is_active ? 'warning' : 'success'}
                                                        onClick={() => handlePackageToggle(pkg.id, pkg.is_active)}
                                                        sx={{ minWidth: 'auto', px: 1 }}
                                                    >
                                                        {pkg.is_active ? 'Deactivate' : 'Activate'}
                                                    </Button>
                                                )}
                                                {checkPermission('Package Management', 'delete') && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="error"
                                                        onClick={() => handlePackageDelete(pkg.id)}
                                                        sx={{ minWidth: 'auto', px: 1 }}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination - using same pattern as FeedbackManagement.js */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Enterprise Modal */}
            <Dialog open={showEnterpriseModal} onClose={() => setShowEnterpriseModal(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Enterprise Package Management
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowEnterpriseModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {enterpriseData ? (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Enterprise Packages ({enterpriseData.length})
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Package Name</TableCell>
                                            <TableCell>Tokens</TableCell>
                                            <TableCell>Price</TableCell>
                                            <TableCell>Validity</TableCell>
                                            <TableCell>Custom Terms</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {enterpriseData.map((pkg, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Typography variant="subtitle2">
                                                        {pkg.name}
                                                    </Typography>
                                                    {pkg.description && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {pkg.description}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {pkg.min_credits && pkg.max_credits ? (
                                                        <Typography variant="body2">
                                                            {pkg.min_credits} - {pkg.max_credits}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2">
                                                            {pkg.credits}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>₹{pkg.price}</TableCell>
                                                <TableCell>{pkg.validity_days} days</TableCell>
                                                <TableCell>
                                                    {pkg.custom_terms ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {pkg.custom_terms}
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Standard terms
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={pkg.is_active ? 'Active' : 'Inactive'} 
                                                        color={pkg.is_active ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : (
                        <Typography>Loading enterprise packages...</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowEnterpriseModal(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Export Modal */}
            <Dialog open={showExportModal} onClose={() => setShowExportModal(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Package Export Data
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowExportModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {exportData ? (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Exported {exportData.length} packages
                            </Typography>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Package Name</TableCell>
                                            <TableCell>Tokens</TableCell>
                                            <TableCell>Price</TableCell>
                                            <TableCell>Validity</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {exportData.map((pkg, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{pkg.name}</TableCell>
                                                <TableCell>{pkg.credits}</TableCell>
                                                <TableCell>₹{pkg.price}</TableCell>
                                                <TableCell>{pkg.validity_days} days</TableCell>
                                                <TableCell>{pkg.package_type}</TableCell>
                                                <TableCell>
                                                    <Chip 
                                                        label={pkg.is_active ? 'Active' : 'Inactive'} 
                                                        color={pkg.is_active ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    ) : (
                        <Typography>Loading export data...</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowExportModal(false)}>Close</Button>
                    <Button 
                        onClick={() => {
                            // In future, implement actual CSV/Excel download
                            showAlert('Download functionality coming soon!', 'info');
                        }}
                        variant="contained"
                        color="primary"
                    >
                        Download CSV
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Package Modal */}
            <Dialog open={showPackageModal} onClose={() => setShowPackageModal(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingPackage ? 'Edit Package' : 'Add New Package'}
                </DialogTitle>
                <form onSubmit={handlePackageSubmit}>
                    <DialogContent>
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Package Name"
                                    value={packageForm.name}
                                    onChange={(e) => handleFieldChange('name', e.target.value)}
                                    error={!!fieldErrors.name}
                                    helperText={fieldErrors.name}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth required>
                                    <InputLabel>Package Type</InputLabel>
                                    <Select
                                        value={packageForm.package_type}
                                        onChange={(e) => {
                                            const newPackageType = e.target.value;
                                            const updatedForm = {...packageForm, package_type: newPackageType};
                                            
                                            // Automatically set price to 0 for FREE packages
                                            if (newPackageType === 'FREE') {
                                                updatedForm.price = '0';
                                            }
                                            
                                            setPackageForm(updatedForm);
                                        }}
                                        label="Package Type"
                                    >
                                        {packageTypes.map(type => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Tokens"
                                    type="number"
                                    value={packageForm.credits}
                                    onChange={(e) => handleFieldChange('credits', e.target.value)}
                                    error={!!fieldErrors.credits}
                                    helperText={fieldErrors.credits}
                                    inputProps={{ min: 1, max: 1000000 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label={packageForm.package_type === 'FREE' ? 'Price (₹) - FREE' : 'Price (₹)'}
                                    type="number"
                                    value={packageForm.package_type === 'FREE' ? '0' : packageForm.price}
                                    onChange={(e) => {
                                        const newPrice = packageForm.package_type === 'FREE' ? '0' : e.target.value;
                                        handleFieldChange('price', newPrice);
                                    }}
                                    disabled={packageForm.package_type === 'FREE'}
                                    error={!!fieldErrors.price}
                                    helperText={fieldErrors.price || (packageForm.package_type === 'FREE' ? 'FREE packages have price 0' : '')}
                                    inputProps={{ min: 0, max: 100000 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Validity (Days)"
                                    type="number"
                                    value={packageForm.validity_days}
                                    onChange={(e) => {
                                        const newValidity = e.target.value;
                                        handleFieldChange('validity_days', newValidity);
                                        setPackageForm({
                                            ...packageForm, 
                                            validity_days: newValidity,
                                            // Auto-set token validity (credit_validity_days) if not specified
                                            credit_validity_days: packageForm.credit_validity_days || newValidity
                                        });
                                    }}
                                    error={!!fieldErrors.validity_days}
                                    helperText={fieldErrors.validity_days}
                                    inputProps={{ min: 1, max: 3650 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Token validity (days)"
                                    type="number"
                                    value={packageForm.credit_validity_days}
                                    onChange={(e) => handleFieldChange('credit_validity_days', e.target.value)}
                                    error={!!fieldErrors.credit_validity_days}
                                    helperText={fieldErrors.credit_validity_days || "How long tokens remain usable"}
                                    inputProps={{ min: 1, max: 3650 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Token expiry extension (days)"
                                    type="number"
                                    value={packageForm.credit_expiry_extension_days}
                                    onChange={(e) => handleFieldChange('credit_expiry_extension_days', e.target.value)}
                                    error={!!fieldErrors.credit_expiry_extension_days}
                                    helperText={fieldErrors.credit_expiry_extension_days || "Grace period after package expiry"}
                                    inputProps={{ min: 0, max: 365 }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    💡 <strong>Dual expiry:</strong> Package expiry controls subscription status; token validity controls how long granted tokens can be used.
                                    Tokens can stay usable after package expiry when an extension is set.
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={3}
                                    value={packageForm.description}
                                    onChange={(e) => setPackageForm({...packageForm, description: e.target.value})}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Sort Order"
                                    type="number"
                                    value={packageForm.sort_order}
                                    onChange={(e) => handleFieldChange('sort_order', e.target.value)}
                                    error={!!fieldErrors.sort_order}
                                    helperText={fieldErrors.sort_order}
                                    inputProps={{ min: 0, max: 999 }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={packageForm.is_active}
                                            onChange={(e) => setPackageForm({...packageForm, is_active: e.target.checked})}
                                        />
                                    }
                                    label="Active Package"
                                />
                            </Grid>
                            
                            {/* Enterprise Package Fields */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={packageForm.is_enterprise}
                                            onChange={(e) => setPackageForm({...packageForm, is_enterprise: e.target.checked})}
                                        />
                                    }
                                    label="Enterprise Package"
                                />
                            </Grid>
                            
                            {packageForm.is_enterprise && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Minimum tokens"
                                            type="number"
                                            value={packageForm.min_credits || ''}
                                            onChange={(e) => handleFieldChange('min_credits', e.target.value || null)}
                                            error={!!fieldErrors.min_credits}
                                            helperText={fieldErrors.min_credits}
                                            inputProps={{ min: 1, max: 1000000 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Maximum tokens"
                                            type="number"
                                            value={packageForm.max_credits || ''}
                                            onChange={(e) => handleFieldChange('max_credits', e.target.value || null)}
                                            error={!!fieldErrors.max_credits}
                                            helperText={fieldErrors.max_credits}
                                            inputProps={{ min: 1, max: 1000000 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Custom Terms"
                                            multiline
                                            rows={3}
                                            value={packageForm.custom_terms}
                                            onChange={(e) => setPackageForm({...packageForm, custom_terms: e.target.value})}
                                        />
                                    </Grid>
                                    {fieldErrors.enterprise && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="error">
                                                {fieldErrors.enterprise}
                                            </Typography>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowPackageModal(false)}>Cancel</Button>
                        <Button type="submit" variant="contained">
                            {editingPackage ? 'Update' : 'Create'} Package
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Alert Snackbar */}
            <Snackbar 
                open={alert.show} 
                autoHideDuration={6000} 
                onClose={() => setAlert({...alert, show: false})}
            >
                <Alert 
                    onClose={() => setAlert({...alert, show: false})} 
                    severity={alert.type}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default AdminPackageManagement; 